import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Dialog } from '@/shared/ui';
import { ROLE_LABEL_KEY, USER_STATUS_I18N_KEY } from '@entities/User';
import type { User } from '@psp-aggregator/sdk';

export interface UserDetailsModalProps {
  onClose: () => void;
  user: User;
}

export const UserDetailsModal: FC<UserDetailsModalProps> = ({
  onClose,
  user,
}) => {
  const { t } = useTranslation();

  const rows: { label: string; value: string }[] = [
    {
      label: t('pages.admin.users.fields.firstName'),
      value: user.first_name || '—',
    },
    {
      label: t('pages.admin.users.fields.lastName'),
      value: user.last_name || '—',
    },
    { label: t('pages.admin.users.fields.email'), value: user.email },
    {
      label: t('pages.admin.users.fields.telegram'),
      value: user.telegram || '—',
    },
    {
      label: t('pages.admin.users.fields.companyName'),
      value: user.company_name || '—',
    },
    {
      label: t('pages.admin.users.fields.role'),
      value: user.role ? t(ROLE_LABEL_KEY[user.role]) : t('roles.pending'),
    },
    {
      label: t('pages.admin.users.fields.status'),
      value: t(USER_STATUS_I18N_KEY[user.status]),
    },
    {
      label: t('pages.admin.users.fields.invitedBy'),
      value: user.invited_by_user_id || '—',
    },
    {
      label: t('pages.admin.users.fields.createdAt'),
      value: new Date(user.created_at).toLocaleString(),
    },
    {
      label: t('pages.admin.users.fields.updatedAt'),
      value: new Date(user.updated_at).toLocaleString(),
    },
  ];

  return (
    <Dialog title={t('pages.admin.users.detailsTitle')} onClose={onClose}>
      <div className="space-y-2">
        {rows.map((row) => (
          <div key={row.label} className="flex justify-between gap-4 text-sm">
            <span className="text-muted-foreground">{row.label}</span>
            <span className="break-all text-right font-medium">
              {row.value}
            </span>
          </div>
        ))}
      </div>
      <Button
        className="mt-5 w-full"
        type="button"
        variant="secondary"
        onClick={onClose}>
        {t('buttons.close')}
      </Button>
    </Dialog>
  );
};

UserDetailsModal.displayName = 'UserDetailsModal';
